import { useEffect, useState } from "react";
import {
  Row,
  Col,
  Button,
  Card,
  Statistic,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  DatePicker,
  Table,
  Spin,
  Popconfirm,
  Tooltip,
} from "antd";
import { DeleteOutlined, PrinterOutlined } from "@ant-design/icons";
import moment from "moment";
import {
  toDeleteReglementClient,
  toDepositWallet,
  toGetWallet,
  toPrintWalletAcomptes,
  toPrintWalletDecharge,
} from "../APIs";
import { successMsg, toConvertDateTime } from "../../../Utils/Utils";

const { Option } = Select;
const { RangePicker } = DatePicker;

const PAYMENT_MODES = [
  { value: 0, label: "Espèces" },
  { value: 1, label: "Chèques" },
  { value: 2, label: "Traites" },
  { value: 3, label: "Carte bancaire" },
  { value: 4, label: "Virement bancaire" },
  { value: 5, label: "Retenu à la source" },
  { value: 6, label: "Contre remboursement" },
  { value: 99, label: "Remise" },
];

const ClientWallet = ({ clientId }) => {
  const [wallet, setWallet] = useState(null);
  const [loading, setLoading] = useState(false);
  const [depositVisible, setDepositVisible] = useState(false);
  const [depositSaving, setDepositSaving] = useState(false);
  const [dateRange, setDateRange] = useState(null);
  const [form] = Form.useForm();

  const loadWallet = () => {
    if (null == clientId) return;
    setLoading(true);
    toGetWallet(clientId).then((res) => {
      setWallet(res);
      setLoading(false);
    });
  };

  useEffect(() => {
    setWallet(null);
    setDateRange(null);
    loadWallet();
  }, [clientId]);

  const closeDeposit = () => {
    setDepositVisible(false);
    form.resetFields();
  };

  const submitDeposit = () => {
    form.validateFields().then((values) => {
      const payment = {
        montant: values.montant,
        type: values.type,
      };
      if (values.type === 1 || values.type === 2) {
        payment.nom = values.nom || "";
        payment.banque = values.banque || "";
        payment.num = values.num || 0;
        payment.date = values.date ? values.date.valueOf() : 0;
        if (values.type === 1) payment.chequeValue = values.montant;
      }
      setDepositSaving(true);
      toDepositWallet(clientId, payment, values.note).then((res) => {
        setDepositSaving(false);
        if (null != res) {
          setWallet({
            ...wallet,
            solde: res.solde,
            mouvements: [res.entry, ...(wallet ? wallet.mouvements : [])],
          });
          closeDeposit();
          successMsg("Dépôt enregistré, le montant est disponible en caisse");
        }
      });
    });
  };

  const printDecharge = (record) => {
    toPrintWalletDecharge(record.id);
  };

  const removeDeposit = (record) => {
    toDeleteReglementClient(record.reglementId).then((deleted) => {
      if (deleted) {
        successMsg("Dépôt supprimé");
        loadWallet();
      }
    });
  };

  const filteredMouvements = (wallet && wallet.mouvements ? wallet.mouvements : []).filter(
    (mouvement) => {
      if (!dateRange || !dateRange[0] || !dateRange[1]) return true;
      const date = moment(mouvement.createdDate);
      if (!date.isValid()) return false;
      return date.isBetween(
        moment(dateRange[0]).startOf("day"),
        moment(dateRange[1]).endOf("day"),
        undefined,
        "[]",
      );
    },
  );

  const filteredDeposits = filteredMouvements.filter(
    (mouvement) => parseInt(mouvement.type) === 0,
  );

  const filteredDebits = filteredMouvements.filter(
    (mouvement) => parseInt(mouvement.type) === 1,
  );

  const filteredDepositsTotal = filteredDeposits.reduce(
    (total, mouvement) => total + (parseFloat(mouvement.value) || 0),
    0,
  );

  const filteredDebitsTotal = filteredDebits.reduce(
    (total, mouvement) => total + (parseFloat(mouvement.value) || 0),
    0,
  );

  const filteredReste = filteredDepositsTotal - filteredDebitsTotal;

  const printAcomptes = () => {
    const start =
      dateRange && dateRange[0] ? dateRange[0].format("YYYY-MM-DD") : "-1";
    const end =
      dateRange && dateRange[1] ? dateRange[1].format("YYYY-MM-DD") : "-1";
    toPrintWalletAcomptes(clientId, start, end);
  };

  const resetDateRange = () => setDateRange(null);

  const columns = [
    {
      title: "Date",
      dataIndex: "createdDate",
      key: "createdDate",
      render: (v) => toConvertDateTime(v),
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      render: (v) =>
        parseInt(v) === 1 ? (
          <span style={{ color: "#ef5350" }}>Débit</span>
        ) : (
          <span style={{ color: "#1b9e4d" }}>Crédit</span>
        ),
    },
    {
      title: "Montant",
      dataIndex: "value",
      key: "value",
      render: (v, record) => (
        <b>
          {parseFloat(record.type) === 1 ? "-" : "+"}{" "}
          {parseFloat(v).toFixed(3)} DT
        </b>
      ),
    },
    {
      title: "Référence",
      dataIndex: "reference",
      key: "reference",
      render: (v, record) => v || record.reglementRef || "-",
    },
    {
      title: "Note",
      dataIndex: "note",
      key: "note",
      render: (v) => v || "-",
    },
    {
      title: "",
      key: "actions",
      width: 90,
      align: "center",
      render: (_, record) => {
        if (parseInt(record.type) !== 0) return null;
        return (
          <span>
            <Tooltip title="Imprimer la décharge">
              <Button
                type="text"
                icon={<PrinterOutlined />}
                onClick={() => printDecharge(record)}
              />
            </Tooltip>
            {record.reglementId && record.reglementId !== -1 && (
              <Popconfirm
                title="Supprimer ce dépôt du wallet ?"
                okText="Oui"
                cancelText="Non"
                onConfirm={() => removeDeposit(record)}
              >
                <Tooltip title="Supprimer le dépôt">
                  <Button type="text" danger icon={<DeleteOutlined />} />
                </Tooltip>
              </Popconfirm>
            )}
          </span>
        );
      },
    },
  ];

  return (
    <div style={{ padding: "1vh" }}>
      <Row gutter={24} style={{ marginBottom: "2vh" }}>
        <Col span={8}>
          <Card>
            <Statistic
              title="Solde wallet"
              value={wallet ? wallet.solde : 0}
              precision={3}
              suffix=" DT"
              valueStyle={{ color: wallet && wallet.solde >= 0 ? "#1b9e4d" : "#ef5350" }}
            />
          </Card>
        </Col>
        <Col span={16}>
          <Row gutter={12} align="middle">
            <Col>
              <Button type="primary" onClick={() => setDepositVisible(true)}>
                Dépôt
              </Button>
            </Col>
            <Col>
              <Button onClick={loadWallet}>Actualiser</Button>
            </Col>
          </Row>
          <Row
            gutter={12}
            align="middle"
            style={{ marginTop: 12, flexWrap: "wrap", rowGap: 8 }}
          >
            <Col>
              <RangePicker
                value={dateRange}
                onChange={setDateRange}
                format="DD/MM/YYYY"
                placeholder={["Date début", "Date fin"]}
              />
            </Col>
            <Col>
              <Button onClick={resetDateRange} disabled={!dateRange}>
                Réinitialiser
              </Button>
            </Col>
            <Col>
              <Button
                type="primary"
                icon={<PrinterOutlined />}
                onClick={printAcomptes}
                disabled={filteredMouvements.length === 0}
              >
                Imprimer le relevé
              </Button>
            </Col>
            <Col>
              <span style={{ color: "#58595b" }}>
                {filteredMouvements.length} mouvement(s) — Crédit :{" "}
                <b>{filteredDepositsTotal.toFixed(3)} DT</b> / Débit :{" "}
                <b>{filteredDebitsTotal.toFixed(3)} DT</b> / Reste :{" "}
                <b>{filteredReste.toFixed(3)} DT</b>
              </span>
            </Col>
          </Row>
        </Col>
      </Row>

      <Spin spinning={loading}>
        <Table
          rowKey={(record, index) => index}
          dataSource={filteredMouvements}
          columns={columns}
          pagination={{ pageSize: 10, hideOnSinglePage: true }}
          size="small"
        />
      </Spin>

      <Modal
        visible={depositVisible}
        title="Dépôt dans le wallet"
        onCancel={closeDeposit}
        onOk={submitDeposit}
        okText="Enregistrer"
        confirmLoading={depositSaving}
        cancelButtonProps={{ style: { display: "none" } }}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label="Montant"
            name="montant"
            rules={[
              { required: true, message: "Merci de saisir le montant" },
              {
                validator: (_, value) =>
                  value && parseFloat(value) > 0
                    ? Promise.resolve()
                    : Promise.reject("Le montant doit être positif"),
              },
            ]}
          >
            <InputNumber
              style={{ width: "100%" }}
              decimalSeparator=","
              min={0}
            />
          </Form.Item>
          <Form.Item
            label="Mode de règlement"
            name="type"
            initialValue={0}
            rules={[{ required: true, message: "Merci de choisir le mode" }]}
          >
            <Select>
              {PAYMENT_MODES.map((mode) => (
                <Option key={mode.value} value={mode.value}>
                  {mode.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item noStyle shouldUpdate>
            {({ getFieldValue }) => {
              const type = getFieldValue("type");
              if (type !== 1 && type !== 2) return null;
              return (
                <>
                  <Form.Item label="Nom" name="nom">
                    <Input />
                  </Form.Item>
                  <Form.Item label="Banque" name="banque">
                    <Input />
                  </Form.Item>
                  <Form.Item label="Numéro" name="num">
                    <InputNumber style={{ width: "100%" }} />
                  </Form.Item>
                  <Form.Item label="Date" name="date">
                    <DatePicker style={{ width: "100%" }} />
                  </Form.Item>
                </>
              );
            }}
          </Form.Item>
          <Form.Item label="Note" name="note">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ClientWallet;
